<template>
  <div ref="menuActivator" class="relative">
    <icon-base
      v-on-click-outside="closeMenu"
      role="menuActivator"
      :icon="IconVerticalDots"
      height="24"
      width="24"
      data-cy="menu-button"
      @click.prevent.stop="toggleMenu"
    />
    <ul
      v-show="showMenu"
      ref="dropdown"
      :style="dropdownStyle"
      class="offer-dropdown-menu z-10 flex w-max transform flex-col items-start justify-start rounded-sm bg-white text-left shadow-md"
    >
      <li class="w-full hover:bg-blue-100">
        <button
          class="block w-full px-4 py-2 text-left"
          @click.prevent.stop="toggleOfferModal"
        >
          {{ $t("COMMON.EDIT") }}
        </button>
      </li>
      <li v-if="!isClosing" class="w-full hover:bg-blue-100">
        <button
          class="block w-full px-4 py-2 text-left"
          @click.prevent.stop="showOfferFields"
        >
          {{ $t("COMMON.EDIT") }} {{ $t("COMMON.SNAPSHOT") }}
          {{ $t("COMMON.DETAIL", 2) }}
        </button>
      </li>
      <li v-if="!isClosing" class="w-full hover:bg-blue-100">
        <button
          class="block w-full px-4 py-2 text-left"
          @click.prevent.stop="duplicateOffer"
        >
          {{ $t("COMMON.DUPLICATE") }}
        </button>
      </li>
      <li class="w-full hover:bg-blue-100">
        <button
          class="block w-full px-4 py-2 text-left"
          :disabled="isWebhookSending"
          @click.prevent.stop="sendWebhook"
        >
          {{ $t("COMMON.SEND") }} {{ $t("ORGANIZATION.API_CONFIG.WEBHOOKS") }}
        </button>
      </li>
      <li class="cursor-pointer">
        <status-menu :offer="model" />
      </li>
      <li class="w-full text-red-400 hover:bg-blue-100">
        <delete-offer-modal
          v-if="showDeleteOfferModal"
          :selected-offers="[model.id]"
          :offers="[model]"
          :close="() => (showDeleteOfferModal = false)"
        />
        <button
          class="block w-full px-4 py-2 text-left"
          @click.prevent.stop="showDeleteOfferModal = true"
        >
          {{ $t("COMMON.DELETE") }}
        </button>
      </li>
    </ul>
    <offer-modal
      v-if="showOfferModal"
      :offer-details="[model.details]"
      edit-mode
      :is-closing="isClosing"
      @close="showOfferModal = false"
    />
    <wfb-offer-fields-modal
      v-if="showOfferFieldsModal && offerTemplate"
      :template="offerTemplate"
      :funder="model.funder"
      is-workflow-live
      @close="() => (showOfferFieldsModal = false)"
      @template-updated="updateOfferTemplate"
    />
  </div>
</template>
<script setup lang="ts">
import { type CSSProperties, computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { usePromiseWrapper } from "@/hooks/common";
import { useTeleportV2 } from "@/hooks/elements";
import { useDropdownMenu } from "@/hooks/menus";
import { useNotification } from "@/hooks/notifications";
import { useOverflowDetection } from "@/hooks/useOverflowDetection";
import applicationsApiService from "@/services/modules/applications";

import WfbOfferFieldsModal from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/WfbOfferFieldsModal.vue";
import IconVerticalDots from "@/components/icons/IconVerticalDots.vue";
import OfferModal from "@/views/deals/components/OfferModal.vue";
import StatusMenu from "@/views/deals/components/StatusMenu.vue";
import DeleteOfferModal from "@/views/deals/modals/DeleteOfferModal.vue";

import type { IOffer } from "@/models/funders";
import type { OfferTemplate } from "@/models/offers";

interface Props {
  model: IOffer;
  isClosing?: boolean;
}

const { model, isClosing = false } = defineProps<Props>();
const { dispatch, commit } = useStore();
const { t } = useI18n();
const { showMessage } = useNotification();

const dropdown = ref<HTMLElement | null>(null);
const showDeleteOfferModal = ref(false);
const showOfferModal = ref(false);
const showOfferFieldsModal = ref(false);
const offerTemplate = ref<OfferTemplate | undefined>();

const { showMenu, toggleMenu, closeMenu, menuActivator } = useDropdownMenu();

const { positionState, setupEventListeners, updatePosition } =
  useOverflowDetection(dropdown, menuActivator, showMenu, {
    scrollableContainerId: "dealsWrapper",
    offset: { top: 10, bottom: 10 },
    checkViewportOverflow: true,
    enableAutoSizing: true,
  });

const { fetchWrapper: getOffer } = usePromiseWrapper(async (id: string) => {
  const offerResponse = await applicationsApiService.getOffer(id, {
    without_files: true,
  });

  offerTemplate.value = {
    name: offerResponse.details.offer_template.name,
    content: offerResponse.details.content,
  } as OfferTemplate;
});

const { fetchWrapper: updateOfferTemplate } = usePromiseWrapper(
  async (template: OfferTemplate) => {
    await applicationsApiService.updateOfferDetails(model.id, template);

    showMessage(t("HTTP.RESPONSE.UPDATE"), "success");
    setTimeout(() => {
      showOfferFieldsModal.value = false;
    }, 500);
  }
);

const { fetchWrapper: sendWebhook, loading: isWebhookSending } =
  usePromiseWrapper(async () => {
    await applicationsApiService.sendOfferAvailableWebhook(model.id);
    showMessage(t("HTTP.RESPONSE.CREATE"), "success");
  });

const dropdownStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    maxHeight: "50vh",
    overflowY: "auto",
  };

  if (positionState.value.maxHeight) {
    const calculatedMaxHeight = positionState.value.maxHeight;
    const halfScreenHeight = window.innerHeight / 2;
    style.maxHeight = `${Math.min(calculatedMaxHeight, halfScreenHeight)}px`;
  }

  if (positionState.value.maxWidth) {
    style.maxWidth = `${positionState.value.maxWidth}px`;
    style.overflowX = "auto";
  }

  return style;
});

const showOfferFields = async () => {
  try {
    commit("setGlobalLoader", true);
    await getOffer(model.id);
    showOfferFieldsModal.value = true;
  } finally {
    commit("setGlobalLoader", false);
  }
};

const duplicateOffer = async () => {
  await dispatch("applications/duplicateOffer", {
    id: model.id,
    ignoreSnapshot: false,
  });

  closeMenu({
    afterClose: () => {
      return;
    },
  });
};

const toggleOfferModal = () => {
  closeMenu({
    afterClose: () => {
      showOfferModal.value = true;
    },
  });
};

useTeleportV2({
  target: dropdown,
  to: "#menus",
  offset: { top: 5, right: 0 },
  checkForOverflow: true,
});

const cleanup = setupEventListeners();

watch(showMenu, (isOpen) => {
  if (isOpen) {
    updatePosition();
  }
});

onBeforeUnmount(() => {
  cleanup();
});
</script>
